import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Star as StarIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import kitchenToolkitBackground from '../images/kitchen-toolkit.png';
import knivesImage from '../images/knives.jpeg';
import cuttingBoardsImage from '../images/cuttingboards.webp';
import castIronSkilletImage from '../images/cast-iron-skillets.jpeg';
import utensilsImage from '../images/utensils.jpg';
import whiskImage from '../images/whisk.jpg';
import measuringImage from '../images/measuring.jpg';
import scaleImage from '../images/scale.jpg';
import mixingBowlsImage from '../images/MixingBowlSet.webp';
import blenderImage from '../images/Blender-Carousel1.webp';

const PAGE_URL = 'https://createadish.com/kitchen-toolkit';
const PAGE_TITLE = 'The Essential Kitchen Toolkit: Must-Have Gear for Every Home Chef - Create A Dish';
const PAGE_DESCRIPTION = 'A comprehensive guide to the fundamental tools—from high-quality chef\'s knives to versatile cast iron skillets—needed to master any AI-generated recipe. This resource ensures you have the right equipment to bring your digital creations to life with professional results.';
const PAGE_IMAGE = 'https://createadish.com/images/kitchen-toolkit-og.png';

const KitchenToolkit: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="kitchen tools, cooking equipment, chef knife, cast iron skillet, cooking tips, kitchen essentials, home chef, cooking resources" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={PAGE_URL} />
        
        {/* Structured Data - Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "The Essential Kitchen Toolkit: Must-Have Gear for Every Home Chef",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": {
              "@type": "Organization",
              "name": "Create A Dish"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Create A Dish",
              "logo": {
                "@type": "ImageObject",
                "url": "https://createadish.com/logo.png"
              }
            },
            "datePublished": "2025-12-24",
            "dateModified": "2025-12-24",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": PAGE_URL
            }
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${kitchenToolkitBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 2,
              }}
            >
              The Essential Kitchen Toolkit
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              Must-Have Gear for Every Home Chef
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Kitchen Toolkit
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Essential Tools for Your Kitchen
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Welcome to CreateADish.com, where AI-powered creativity meets culinary excellence. Whether you're experimenting with our AI-generated recipes or whipping up your own masterpieces, having the right tools in your kitchen is key to achieving professional results. This comprehensive guide highlights the fundamental equipment every home chef needs—from sharp knives to sturdy pans—to bring those digital ideas to life.
          </Typography>

          {/* Knives Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Knives: The Foundation of Prep Work
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              A high-quality chef's knife is indispensable for chopping, slicing, and dicing with precision. Look for one with a comfortable handle and a sharp, durable blade that holds its edge well.
            </Typography>

            <Box
              component="img"
              src={knivesImage}
              alt="Professional chef's knives on cutting board"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Pair it with a paring knife for smaller tasks like peeling fruits or deveining shrimp, and a serrated knife for bread and tomatoes. Experts recommend investing in forged steel blades for longevity.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              A sturdy cutting board provides a safe surface for all your knife work. Wooden boards are gentle on blades and add a rustic charm, while plastic ones are easy to sanitize.
            </Typography>

            <Box
              component="img"
              src={cuttingBoardsImage}
              alt="Various cutting boards - wooden and plastic"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Choose one that's large enough for your needs but not too bulky for storage.
            </Typography>
          </Box>

          {/* Pans and Pots Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Pans and Pots: Versatile Cooking Vessels
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              No kitchen is complete without a versatile cast iron skillet. It's perfect for searing meats, baking cornbread, or even roasting vegetables, thanks to its excellent heat retention and natural non-stick properties once seasoned.
            </Typography>

            <Box
              component="img"
              src={castIronSkilletImage}
              alt="Cast iron skillets"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Complement it with a non-stick skillet for eggs and delicate foods, and a saucepan for sauces or boiling pasta.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              For heartier dishes, a Dutch oven is ideal for braising, stewing, and baking. Its heavy lid traps moisture, making it great for slow-cooked AI-inspired recipes like stews or casseroles.
            </Typography>
          </Box>

          {/* Utensils Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Utensils: Everyday Helpers
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Stainless steel tongs are a go-to for flipping burgers, tossing salads, or serving pasta without a mess.
            </Typography>

            <Box
              component="img"
              src={utensilsImage}
              alt="Kitchen utensils including tongs and spatulas"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              They're heat-resistant and provide a firm grip.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              A wire whisk excels at incorporating air into batters, emulsifying dressings, or smoothing out lumps in sauces.
            </Typography>

            <Box
              component="img"
              src={whiskImage}
              alt="Wire whisk"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Opt for one with a comfortable handle for extended use.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Don't forget a spatula for scraping bowls and flipping pancakes, and wooden spoons for stirring without scratching your cookware.
            </Typography>
          </Box>

          {/* Measuring Tools Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Measuring Tools: Precision for Perfect Results
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Accurate measurements are crucial, especially with AI-generated recipes that might call for exact ratios. A set of measuring cups and spoons handles dry and wet ingredients effortlessly.
            </Typography>

            <Box
              component="img"
              src={measuringImage}
              alt="Measuring cups and spoons"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              For even greater accuracy, especially in baking, a digital kitchen scale weighs ingredients down to the gram.
            </Typography>

            <Box
              component="img"
              src={scaleImage}
              alt="Digital kitchen scale"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              It's a game-changer for consistent outcomes.
            </Typography>
          </Box>

          {/* Mixing and Processing Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Mixing and Processing: Prep Essentials
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              A set of mixing bowls in various sizes allows for organizing ingredients and combining them without spillover.
            </Typography>

            <Box
              component="img"
              src={mixingBowlsImage}
              alt="Mixing bowl set"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Stackable options save space.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              For blending soups, smoothies, or purees, a high-speed blender is invaluable.
            </Typography>

            <Box
              component="img"
              src={blenderImage}
              alt="High-speed blender"
              sx={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
                display: 'block',
                boxShadow: 3
              }}
            />

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              It handles tough ingredients like nuts and ice with ease.
            </Typography>
          </Box>

          {/* Baking Basics Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Baking Basics
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              A rimmed baking sheet is essential for roasting veggies, baking cookies, or catching drips under pies. Aluminum ones heat evenly and are durable.
            </Typography>
          </Box>

          {/* Final Thoughts Section */}
          <Box sx={{ mb: 4, p: 4, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main',
                textAlign: 'center'
              }}
            >
              Final Thoughts
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 0,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' },
                textAlign: 'center',
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Equipping your kitchen with these essentials ensures you're ready to tackle any recipe from CreateADish.com. Start with the basics like knives and pans, then build from there. With the right gear, your home-cooked meals will rival those from professional kitchens. Happy cooking!
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Ready to Create Your Perfect Dish?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<StarIcon />}
              onClick={handleGetStarted}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              Get Started
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default KitchenToolkit;
